import { isEqual, last } from 'lodash';
import { addDays } from 'date-fns';

import { formatDate } from '@lt/components/utils/date';

import { getCornerDates } from './components/DatePicker/components/Calendar/utils';
import type { PreviousSearchesOption, Values } from './context';
import { DateCell } from './components/DatePicker/types';
import { enToRusKeyboardMap, FIELD_NAMES } from './constants';

export const lastStep = (steps: string[]): string | undefined => last(steps);
export const isDestinationStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.destination;
export const afterDestinationStep = (steps: string[]): boolean =>
  steps.includes(FIELD_NAMES.destination);
export const isDepartureStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.departure;
export const isDatepickerStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.datepicker;
export const afterDepartureStep = (steps: string[]): boolean =>
  steps.includes(FIELD_NAMES.departure);
export const isPopularCountriesStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.popularCountries;
export const isMetaregionsStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.metaregions;
export const afterDatepickerStep = (steps: string[]): boolean =>
  steps.includes(FIELD_NAMES.datepicker);
export const isTouristsStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.tourists;

export const convertDatesToStringWithEllipsis = (
  dateCell?: DateCell,
  isShort?: boolean
): string => {
  if (!dateCell?.date) return '';

  const {
    date,
    shift: { left, right },
  } = dateCell;
  if (left === 0 && right === 0) {
    return formatDate(date, 'd MMM');
  }
  const separator = isShort ? '...' : ' ... ';
  return `${formatDate(addDays(date, -left), 'd MMM')}${separator}${formatDate(
    addDays(date, right),
    'd MMM'
  )}`;
};

export const isEqualPreviousSearches = (
  s1: PreviousSearchesOption,
  s2: PreviousSearchesOption
): boolean => {
  const destinationEqual = s1.destination.value === s2.destination.value;

  const s1Start = getCornerDates(s1.datepicker.left);
  const s1End = getCornerDates(s1.datepicker.right);
  const s2Start = getCornerDates(s2.datepicker.left);
  const s2End = getCornerDates(s2.datepicker.right);
  const datepickerEqual = isEqual(s1Start, s2Start) && isEqual(s1End, s2End);

  const touristEqual = isEqual(s1.tourists, s2.tourists);
  const departureEqual = s1.departure?.value === s2.departure?.value;

  return destinationEqual && datepickerEqual && touristEqual && departureEqual;
};

export const convertLatinToRus = (str: string): string =>
  str
    .toLowerCase()
    .split('')
    .map((sym) => enToRusKeyboardMap.get(sym) || sym)
    .join('');

export const getSearchParamsChanges = (lhs: Values, rhs: Values): Partial<Values> => {
  const changes = Object.keys(lhs).reduce((res, cur) => {
    if (!isEqual(lhs[cur], rhs[cur])) {
      return { ...res, [cur]: rhs[cur] };
    }

    return res;
  }, {});

  return changes;
};
